import { createFileRoute, notFound } from "@tanstack/react-router";
import { Mail, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { PageHeader } from "~/components/shell/app-shell";
import { ThemeSegmented } from "~/components/shell/theme-toggle";
import { Badge, StageBadge, stageColorPresets } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox, RadioGroup, RadioGroupItem, Switch } from "~/components/ui/choice";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { DatePicker } from "~/components/ui/date-picker";
import { SimpleDialog } from "~/components/ui/dialog";
import { Alert, Avatar, EmptyState, Kbd, Skeleton } from "~/components/ui/feedback";
import { Field, Label } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from "~/components/ui/menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { SegmentedControl, Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { toast } from "~/components/ui/toaster";

export const Route = createFileRoute("/_app/design-system")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  component: DesignSystemPage,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-h2 text-fg">{title}</h2>
      {children}
    </section>
  );
}

const textTokens = [
  ["fg", "text-fg"],
  ["fg-secondary", "text-fg-secondary"],
  ["fg-muted", "text-fg-muted"],
  ["primary", "text-primary"],
] as const;

function DesignSystemPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [date, setDate] = useState<Date | null>(() => new Date());
  const [range, setRange] = useState<"7d" | "30d" | "90d">("30d");
  const [reduced, setReduced] = useState(false);

  return (
    <div className="flex flex-col gap-10">
      <PageHeader
        title="Design system"
        description="Every Purlink primitive in one place. Development builds only."
        actions={
          <>
            <div className="flex items-center gap-2">
              <Switch
                id="ds-reduce-transparency"
                checked={reduced}
                onCheckedChange={(on) => {
                  setReduced(on);
                  document.documentElement.classList.toggle("reduce-transparency", on);
                }}
              />
              <Label htmlFor="ds-reduce-transparency" className="text-small">
                Reduce transparency
              </Label>
            </div>
            <ThemeSegmented />
          </>
        }
      />

      <Section title="Typography">
        <Card className="flex flex-col gap-2">
          <p className="text-display text-fg" data-tabular>
            1,248
          </p>
          <p className="text-h1 text-fg">Page title — Leads</p>
          <p className="text-h2 text-fg">Section title — This week</p>
          <p className="text-h3 text-fg">Card title — Hot Lead</p>
          <p className="text-body text-fg">
            Body — Connecting businesses with the people that matter.
          </p>
          <p className="text-body text-fg-secondary">Secondary — Enquired about website design.</p>
          <p className="text-small text-fg-muted">Small — Last contacted 2 days ago</p>
          <p className="text-label text-fg-secondary">LABEL — Preferred contact</p>
          <p className="text-caption text-fg-muted">Caption — Updated 14 Oct 2026, 09:41</p>
        </Card>
      </Section>

      <Section title="Contrast specimen (text on glass)">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(["glass-subtle", "glass", "glass-strong", "surface-card"] as const).map((surface) => (
            <div key={surface} className={`${surface} flex flex-col gap-1 rounded-lg p-4`}>
              <p className="text-caption text-fg-muted">{surface}</p>
              {textTokens.map(([name, cls]) => (
                <p key={name} className={`text-body ${cls}`}>
                  {name}: The quick brown fox
                </p>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap items-center gap-2">
          <Button>
            <Plus /> Add lead
          </Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="soft">Soft</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">
            <Trash2 /> Delete
          </Button>
          <Button variant="link">Link</Button>
          <Button loading>Saving</Button>
          <Button disabled>Disabled</Button>
          <Button size="sm">Small</Button>
          <Button size="icon" variant="ghost" aria-label="Edit">
            <Pencil />
          </Button>
        </div>
      </Section>

      <Section title="Form controls">
        <Card className="grid gap-4 md:grid-cols-2">
          <Field label="First name" required>
            <Input placeholder="Jane" />
          </Field>
          <Field label="Email" error="Please enter a valid email address.">
            <Input defaultValue="jane@" />
          </Field>
          <Field label="Service" description="What they enquired about.">
            <Select defaultValue="seo">
              <SelectTrigger>
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="web">Website design &amp; development</SelectItem>
                <SelectItem value="seo">Search engine optimisation (SEO)</SelectItem>
                <SelectItem value="ppc">Pay-per-click advertising (PPC)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Follow-up date">
            <DatePicker value={date} onChange={setDate} />
          </Field>
          <Field label="Message" className="md:col-span-2">
            <Textarea placeholder="Tell us about your project…" />
          </Field>
          <div className="flex items-center gap-2">
            <Checkbox id="ds-consent" defaultChecked />
            <Label htmlFor="ds-consent">Marketing consent</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="ds-alerts" defaultChecked />
            <Label htmlFor="ds-alerts">New-lead alerts</Label>
          </div>
          <RadioGroup defaultValue="email" aria-label="Preferred contact" className="flex gap-4">
            {["email", "phone", "either"].map((value) => (
              <div key={value} className="flex items-center gap-2">
                <RadioGroupItem id={`ds-${value}`} value={value} />
                <Label htmlFor={`ds-${value}`} className="capitalize">
                  {value}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <SegmentedControl
            ariaLabel="Date range"
            value={range}
            onValueChange={setRange}
            options={[
              { value: "7d", label: "7 days" },
              { value: "30d", label: "30 days" },
              { value: "90d", label: "90 days" },
            ]}
          />
        </Card>
      </Section>

      <Section title="Badges">
        <div className="flex flex-wrap gap-2">
          <Badge>Neutral</Badge>
          <Badge tone="primary">Primary</Badge>
          <Badge tone="success">Delivered</Badge>
          <Badge tone="warning">Delayed</Badge>
          <Badge tone="error">Bounced</Badge>
          <Badge tone="info">Sent</Badge>
          <Badge tone="outline">Outline</Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stageColorPresets).map(([name, color]) => (
            <StageBadge key={name} color={color}>
              {name}
            </StageBadge>
          ))}
          <StageBadge color="#e0218a">custom #e0218a</StageBadge>
        </div>
      </Section>

      <Section title="Alerts">
        <div className="grid gap-2 md:grid-cols-2">
          <Alert tone="info" title="Heads up">
            Opening a lead marks it as reviewed.
          </Alert>
          <Alert tone="success" title="Email delivered" />
          <Alert tone="warning" title="Follow-up overdue">
            Due 2 days ago.
          </Alert>
          <Alert tone="error" title="Email bounced">
            Check the lead's address.
          </Alert>
        </div>
      </Section>

      <Section title="Overlays">
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setDialogOpen(true)}>
            Open dialog
          </Button>
          <Button variant="secondary" onClick={() => setSheetOpen(true)}>
            Open sheet
          </Button>
          <Button variant="secondary" onClick={() => setConfirmOpen(true)}>
            Typed confirmation
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary">
                <MoreHorizontal /> Menu
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>
                <Mail /> Email lead
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Pencil /> Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive>
                <Trash2 /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Tooltip content="Tooltips explain icon-only controls">
            <Button variant="secondary">Hover me</Button>
          </Tooltip>
          <Button
            variant="secondary"
            onClick={() =>
              toast.success("Moved to Hot Lead", { action: { label: "Undo", onClick: () => {} } })
            }
          >
            Toast
          </Button>
          <Button
            variant="secondary"
            onClick={() => toast.error("Couldn't move the lead. Changes were undone.")}
          >
            Error toast
          </Button>
        </div>
        <SimpleDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          title="Add lead"
          description="Leads added here skip the public form."
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setDialogOpen(false)}>Save</Button>
            </>
          }
        >
          <Field label="Email">
            <Input type="email" />
          </Field>
        </SimpleDialog>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Email Jane Doe</SheetTitle>
              <SheetDescription>Replies go to your own inbox.</SheetDescription>
            </SheetHeader>
            <Field label="Subject">
              <Input />
            </Field>
            <SheetFooter>
              <Button onClick={() => setSheetOpen(false)}>Send</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Delete “Cold Lead” and 12 leads?"
          description="The stage and every lead in it will be removed."
          confirmLabel="Delete stage"
          confirmationText="DELETE ALL LEADS"
          destructive
          onConfirm={() => setConfirmOpen(false)}
        />
      </Section>

      <Section title="Tabs">
        <Card>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="emails">Emails</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">Contact details and enquiry.</TabsContent>
            <TabsContent value="activity">Timeline.</TabsContent>
            <TabsContent value="emails">Sent emails.</TabsContent>
          </Tabs>
        </Card>
      </Section>

      <Section title="Table">
        <Card padding="none" className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Service</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                ["1", "Sarah Chen", "rose", "Hot Lead", "Web design"],
                ["2", "Tom Ng", "amber", "Warm Lead", "SEO"],
                ["3", "Priya Shah", "sky", "Cold Lead", "Paid ads"],
              ].map(([rank, name, color, stage, serviceName]) => (
                <TableRow key={rank}>
                  <TableCell data-tabular className="text-fg-muted">
                    {rank}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2.5">
                      <Avatar name={name!} size="sm" /> {name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StageBadge color={stageColorPresets[color as "rose"]}>{stage}</StageBadge>
                  </TableCell>
                  <TableCell className="text-fg-secondary">{serviceName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            summary="1–3 of 42"
            hasPrevious={false}
            hasNext
            onPrevious={() => {}}
            onNext={() => {}}
          />
        </Card>
      </Section>

      <Section title="Cards, loading & empty states">
        <div className="grid gap-3 md:grid-cols-3">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>New leads</CardTitle>
                <CardDescription>Last 30 days</CardDescription>
              </div>
              <Badge tone="success">+12%</Badge>
            </CardHeader>
            <p className="text-display text-fg" data-tabular>
              86
            </p>
          </Card>
          <Card className="flex flex-col gap-3">
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-10 w-1/3" />
            <Skeleton className="h-4 w-full" />
          </Card>
          <Card padding="none">
            <EmptyState
              icon={<Mail />}
              title="No emails yet"
              description="Emails you send to this lead will appear here."
              action={<Button size="sm">Write an email</Button>}
            />
          </Card>
        </div>
        <p className="text-small text-fg-muted">
          Command menu: press <Kbd>⌘</Kbd> <Kbd>K</Kbd>
        </p>
      </Section>
    </div>
  );
}
